import type { BillingRunItem } from '@/services/billing-services'

export type PaymentStatus = 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED'

/**
 * Calculates the progress of a billing run based on payment status
 * Progress is considered as: (PAID + FAILED) / (TOTAL - CANCELLED) * 100
 * Both PAID and FAILED are considered "completed" status
 * Cancelled items are written off and drop out of the denominator entirely
 */
export function calculateBillingProgress(items: BillingRunItem[]): number {
  const openItems = items.filter(item => item.paymentStatus !== 'CANCELLED')
  if (!openItems.length) return 0

  const completedCount = openItems.filter(item =>
    item.paymentStatus === 'PAID' || item.paymentStatus === 'FAILED'
  ).length

  return Math.round((completedCount * 100) / openItems.length)
}

/**
 * Determines if a billing run is fully completed (100% progress)
 */
export function isBillingRunCompleted(items: BillingRunItem[]): boolean {
  return calculateBillingProgress(items) === 100
}

/**
 * Gets the progress text for display
 */
export function getProgressText(progress: number, t: (key: string) => string): string {
  return progress === 100 ? t('progress.complete') : `${progress}% ${t('progress.processed')}`
}

/**
 * Gets the progress color class
 */
export function getProgressColor(progress: number): string {
  return progress === 100 ? 'bg-green-500' : 'bg-blue-500'
}